import * as THREE from "three";
import { generateMap, blockKey, B, type Vec3i, type World } from "../shared/world";
import { newBody, step, EYE, WALK, SPRINT, SNEAK, PLAYER_W, PLAYER_H, type Body } from "../shared/physics";
import { ITEMS, HOTBAR, type Stack } from "../shared/items";
import type { ChestInfo, Phase, PlayerInfo, ServerMsg, SignInfo, ClientMsg } from "../shared/protocol";
import { buildWorldMeshes } from "./mesher";
import { atlas, pixelTexture, ROD_TIP } from "./textures";
import { PlayerModel, ChestModel, SignModel, Particles, itemGeometry, itemMaterial } from "./entities";
import { paintSkin, mcBoxUV } from "./skin";
import { Hud } from "./hud";
import { SignEditor } from "./signedit";
import { Sound, surfaceOf } from "./sound";
import { nearestPlayer, needleAngle } from "../shared/compass";
import { BOW_FULL_DRAW, BOW_MIN_DRAW } from "../shared/projectile";
import { ProjectileView } from "./projectiles";

const SKY_TOP = new THREE.Color("#78a7ff");
const HORIZON = new THREE.Color("#c0d8ff");
const VOID = new THREE.Color("#5f7fc4");
const FOV = 70;
const REACH_HIT = 3.6;
const REACH_USE = 5;

type Hit =
  | { kind: "block"; x: number; y: number; z: number; nx: number; ny: number; nz: number; dist: number }
  | { kind: "chest"; chest: ChestModel; dist: number }
  | { kind: "sign"; sign: SignModel; dist: number }
  | { kind: "player"; model: PlayerModel; dist: number };

export class Game {
  renderer: THREE.WebGLRenderer;
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(FOV, 1, 0.05, 300);
  private worldGroup: THREE.Group | null = null;
  private world: World | null = null;
  private seed = -1;
  private center: Vec3i = { x: 0, y: 0, z: 0 };
  private sky: THREE.Group;
  private clouds: THREE.Mesh;
  private outline: THREE.LineSegments;

  // First person
  private viewModel = new THREE.Group();
  private fpArm: THREE.Mesh;
  private fpItem: THREE.Mesh;
  private fpSkinFor = "";
  private handSwing = 1;
  private equipT = 1;
  private walkDist = 0;
  private bob = 0;
  private hurtT = 0;
  /** 0 = first person, 1 = behind you, 2 = facing you. F5 cycles, like Minecraft. */
  view = 0;
  private selfModel: PlayerModel | null = null;
  private selfSwing = 1;
  private fov = FOV;
  private cloudDrift = 0;

  myId = 0;
  myName = "";
  roomId = "";
  active = false;
  /** Pocket Edition touch scheme: no crosshair, you tap what you want. */
  touchMode = false;
  /** Where the mouse cursor points when there's no pointer lock (drag-to-look); null = screen centre. */
  aim: THREE.Vector2 | null = null;
  body: Body = newBody(0, 30, 0);
  yaw = 0;
  pitch = 0;
  frozen = false;
  alive = true;
  phase: Phase = "waiting";
  phaseEnds = 0;
  hp = 20;
  hotbar: Stack[] = Array(HOTBAR).fill(null);
  armor: (string | null)[] = [null, null, null, null];
  slot = 0;

  private infos = new Map<number, PlayerInfo>();
  private models = new Map<number, PlayerModel>();
  private swingFlag = new Map<number, boolean>();
  private chests = new Map<number, ChestModel>();
  private chestAt = new Map<number, ChestModel>();
  private signs = new Map<number, SignModel>();
  private signGroup = new THREE.Group();
  private entityGroup = new THREE.Group();
  private particles = new Particles();
  private proj = new ProjectileView(this.particles);
  /** When you started pulling your bow back (performance.now), 0 if you aren't. */
  drawStart = 0;
  /** Which sprite the first-person item is showing (bows change as you pull). */
  private fpGeoId = "";
  private drawBlend = 0;

  keys = new Set<string>();
  touchMove = { f: 0, s: 0, jump: false };
  sneakToggle = false;
  sprint = false;
  autoJump = true;
  private lastSend = 0;
  private lastAttack = 0;
  sound = new Sound();
  private stepAt = 0;
  private wasInWater = false;
  private lastTick = -1;
  private otherSteps = new Map<number, { x: number; z: number; d: number }>();

  constructor(canvas: HTMLCanvasElement, public send: (m: ClientMsg) => void, public hud: Hud, public signEditor: SignEditor) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
    this.scene.background = HORIZON;
    this.scene.fog = new THREE.Fog(HORIZON, 26, 72);
    this.camera.rotation.order = "YXZ";
    this.scene.add(new THREE.AmbientLight(0xffffff, 1.7));
    const sun = new THREE.DirectionalLight(0xffffff, 1.5);
    sun.position.set(0.3, 1, 0.5);
    this.scene.add(sun, this.signGroup, this.entityGroup, this.particles.points, this.proj.group, this.camera);

    this.sky = makeSky();
    this.scene.add(this.sky);
    this.clouds = makeClouds();
    this.scene.add(this.clouds);

    // Thin black box around whatever the crosshair is on.
    this.outline = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)),
      new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.45 }),
    );
    this.outline.visible = false;
    this.scene.add(this.outline);

    // Viewmodel: Steve's bare arm, or the held item extruded in 3D.
    this.fpArm = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshLambertMaterial({ depthTest: false }));
    this.fpArm.renderOrder = 20;
    this.fpItem = new THREE.Mesh(new THREE.BufferGeometry(), itemMaterial.clone());
    (this.fpItem.material as THREE.MeshBasicMaterial).depthTest = false;
    this.fpItem.renderOrder = 21;
    this.viewModel.add(this.fpArm, this.fpItem);
    this.camera.add(this.viewModel);
  }

  resize() {
    const c = this.renderer.domElement;
    const w = c.clientWidth, h = c.clientHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  // ---------- room lifecycle ----------

  load(m: Extract<ServerMsg, { t: "joined" }>) {
    this.unloadEntities();
    if (m.seed !== this.seed) {
      if (this.worldGroup) {
        this.scene.remove(this.worldGroup);
        this.worldGroup.traverse((o) => o instanceof THREE.Mesh && o.geometry.dispose());
      }
      const map = generateMap(m.seed);
      this.world = map.world;
      this.center = map.center;
      this.seed = m.seed;
      this.worldGroup = buildWorldMeshes(this.world, pixelTexture(atlas()));
      this.scene.add(this.worldGroup);
      this.proj.setWorld(this.world, this.solid);
    }
    if (this.fpSkinFor !== this.myName) {
      this.fpSkinFor = this.myName;
      if (this.selfModel) {
        this.scene.remove(this.selfModel.root);
        this.selfModel.dispose();
      }
      this.selfModel = new PlayerModel(this.myId, this.myName);
      this.selfModel.showTag = false;
      this.selfModel.root.visible = false;
      this.scene.add(this.selfModel.root);
      const geo = new THREE.BoxGeometry(4 / 16, 12 / 16, 4 / 16);
      mcBoxUV(geo, 40, 16, 4, 12, 4);
      this.fpArm.geometry.dispose();
      this.fpArm.geometry = geo;
      const mat = this.fpArm.material as THREE.MeshLambertMaterial;
      mat.map?.dispose();
      mat.map = pixelTexture(paintSkin(this.myName));
      mat.needsUpdate = true;
    }
    this.roomId = m.room;
    for (const p of m.players) this.addPlayer(p);
    for (const c of m.chests) this.addChest(c);
    for (const s of m.signs) this.addSign(s);
    this.setPhase(m.phase, m.timeLeft);
    this.active = true;
    this.hud.show(true);
    this.hud.clearFeed();
    this.hud.feed(`Welcome to ${m.roomName}!`, "#ffff55");
  }

  unload() {
    this.active = false;
    this.unloadEntities();
    this.hud.show(false);
  }

  private unloadEntities() {
    for (const m of this.models.values()) {
      this.entityGroup.remove(m.root);
      m.dispose();
    }
    for (const c of this.chests.values()) this.entityGroup.remove(c.root);
    for (const s of this.signs.values()) {
      this.signGroup.remove(s.root);
      s.dispose();
    }
    this.models.clear();
    this.infos.clear();
    this.chests.clear();
    this.chestAt.clear();
    this.signs.clear();
    this.proj.clear();
    this.drawStart = 0;
  }

  private key(x: number, y: number, z: number) {
    return blockKey(x, y, z);
  }

  private addPlayer(p: PlayerInfo) {
    this.infos.set(p.id, p);
    if (p.id === this.myId) {
      this.alive = p.alive;
      return;
    }
    let m = this.models.get(p.id);
    if (!m) {
      m = new PlayerModel(p.id, p.name);
      m.root.visible = false;
      this.models.set(p.id, m);
      this.entityGroup.add(m.root);
    }
    m.setArmor(p.armor);
  }

  private addChest(c: ChestInfo) {
    // Latch faces away from the arena centre, toward the pedestals.
    const dx = c.x - this.center.x, dz = c.z - this.center.z;
    const yaw = dx || dz ? Math.atan2(-dx, -dz) : 0;
    const m = new ChestModel(c, Math.round(yaw / (Math.PI / 2)) * (Math.PI / 2));
    this.chests.set(c.id, m);
    this.chestAt.set(this.key(c.x, c.y, c.z), m);
    this.entityGroup.add(m.root);
  }

  private addSign(s: SignInfo) {
    const m = new SignModel(s);
    this.signs.set(s.id, m);
    this.signGroup.add(m.root);
  }

  private setPhase(phase: Phase, timeLeft: number) {
    this.phase = phase;
    this.phaseEnds = performance.now() + timeLeft * 1000;
  }

  solid = (x: number, y: number, z: number) => this.world!.solid(x, y, z) || this.chestAt.has(this.key(x, y, z));

  // ---------- network ----------

  handle(m: ServerMsg) {
    switch (m.t) {
      case "phase":
        this.setPhase(m.phase, m.timeLeft);
        if (m.phase === "playing") this.sound.play("start");
        if (m.phase === "countdown") this.hud.title("Survival Games", "Last one standing wins", 3000);
        break;
      case "snap": {
        const now = performance.now();
        const seen = new Set<number>();
        for (const [id, x, y, z, yaw, pitch, flags, held, hp] of m.p) {
          if (id === this.myId) continue;
          const model = this.models.get(id);
          if (!model) continue;
          seen.add(id);
          if (!model.root.visible) {
            model.snaps = [];
            Object.assign(model, { x, y, z, yaw, pitch });
          }
          model.root.visible = true;
          model.snaps.push({ t: now, x, y, z, yaw, pitch });
          model.sneak = (flags & 1) !== 0;
          const sw = (flags & 2) !== 0;
          if (sw && !this.swingFlag.get(id)) model.swing();
          this.swingFlag.set(id, sw);
          model.drawing = (flags & 4) !== 0;
          model.setHeld(model.drawing && held === "bow" ? "bow_pulling_2" : held === "fishing_rod" && this.proj.hasBobber(id) ? "fishing_rod_cast" : held);
          model.setHealth(hp);
        }
        for (const [id, model] of this.models) if (!seen.has(id)) model.root.visible = false;
        break;
      }
      case "pjoin":
      case "pinfo": {
        const wasAlive = this.alive;
        this.addPlayer(m.p);
        if (m.p.id === this.myId) {
          this.alive = m.p.alive;
          if (wasAlive && !m.p.alive && (this.phase === "playing" || this.phase === "deathmatch")) {
            this.hud.died();
            this.sound.play("death");
          }
        }
        break;
      }
      case "pleave": {
        const model = this.models.get(m.id);
        if (model) {
          this.entityGroup.remove(model.root);
          model.dispose();
        }
        this.models.delete(m.id);
        this.infos.delete(m.id);
        this.otherSteps.delete(m.id);
        break;
      }
      case "self": {
        const before = this.hotbar[this.slot]?.id;
        this.hp = m.hp;
        this.hotbar = m.hotbar;
        this.armor = m.armor;
        this.slot = m.slot;
        this.selfModel?.setArmor(m.armor);
        this.hud.setStats(m.hp, m.armor);
        this.hud.setHotbar(m.hotbar, m.slot);
        if (before !== this.hotbar[this.slot]?.id) this.equipT = 0;
        this.updateHeld();
        break;
      }
      case "tp":
        this.body = newBody(m.x, m.y, m.z);
        this.yaw = m.yaw;
        this.pitch = 0;
        this.frozen = m.frozen;
        break;
      case "hurt":
        this.body.vx += m.kx;
        this.body.vz += m.kz;
        if (m.ky) {
          this.body.vy = m.ky;
          this.body.onGround = false;
        }
        this.hurtT = 1;
        this.hud.hurt();
        break;
      case "hit": {
        const model = this.models.get(m.id);
        model?.hurt();
        if (m.id === this.myId) this.sound.play(m.crit ? "crit" : "hurt");
        else if (model) this.sound.play(m.crit ? "crit" : "hurt", { x: model.x, y: model.y + 1, z: model.z });
        if (m.crit) {
          // The burst of little stars, around whoever got crit (including you).
          const [x, y, z] = model ? [model.x, model.y, model.z] : m.id === this.myId ? [this.body.x, this.body.y, this.body.z] : [NaN, 0, 0];
          if (!Number.isNaN(x)) this.particles.crit(x, y + 0.9, z);
        }
        break;
      }
      case "chest": {
        const c = this.chests.get(m.id);
        if (c) {
          if (m.open && !c.open) this.sound.play("chestOpen", { x: c.info.x + 0.5, y: c.info.y + 0.5, z: c.info.z + 0.5 });
          c.open = m.open;
        }
        break;
      }
      case "chestAdd":
        this.addChest(m.c);
        break;
      case "loot":
        this.hud.toast(m.items.length ? `+ ${m.items.join(", ")}` : "Nothing you need in here");
        if (m.items.length) setTimeout(() => this.sound.play("pop"), 250);
        break;
      case "signAdd":
        this.addSign(m.s);
        this.sound.play("sign", { x: m.s.x + 0.5, y: m.s.y + 0.5, z: m.s.z + 0.5 });
        break;
      case "signDel": {
        const s = this.signs.get(m.id);
        if (s) {
          this.sound.play("signBreak", { x: s.info.x + 0.5, y: s.info.y + 0.5, z: s.info.z + 0.5 });
          this.signGroup.remove(s.root);
          s.dispose();
          this.signs.delete(m.id);
        }
        break;
      }
      case "feed":
        this.hud.feed(m.msg, m.msg.includes(" won ") ? "#ffaa00" : m.msg.includes(" joined") || m.msg.includes(" left") ? "#ffff55" : "#fff");
        break;
      case "chat":
        this.hud.chat(m.from, m.text);
        this.sound.play("chat");
        break;
      case "alive":
        this.hud.setAlive(m.n);
        break;
      case "win":
        if (m.name === this.myName) this.sound.play("win");
        if (m.name === this.myName) this.hud.title("You won!", "Last tribute standing", 7000);
        else this.hud.title(m.name ? `${m.name} won!` : "Nobody won!", "Next round soon...", 7000);
        break;
      case "toast":
        this.hud.toast(m.msg);
        break;
      case "proj": {
        const p = m.p;
        const at = { x: p.x, y: p.y, z: p.z };
        if (this.proj.set(p)) {
          if (p.state === 0) this.sound.play(p.kind === "arrow" ? "bow" : "cast", p.by === this.myId ? undefined : at);
        } else if (p.state === 1 && p.kind === "arrow") this.sound.play("arrowHit", at);
        break;
      }
      case "projDel": {
        const p = this.proj.remove(m.id);
        if (p && m.pop) this.sound.play("pop", p);
        if (p?.kind === "bobber") this.sound.play("reel", p.by === this.myId ? undefined : this.playerAt(p.by) ?? undefined);
        break;
      }
      case "ding":
        this.sound.play("ding");
        break;
      case "refill":
        this.hud.feed("Chests have been refilled!", "#ffff55");
        this.sound.play("refill");
        break;
    }
  }

  // ---------- actions ----------

  selectSlot(i: number) {
    if (i < 0 || i >= HOTBAR) return;
    if (i !== this.slot) this.equipT = 0;
    this.slot = i;
    this.send({ t: "slot", i });
    this.hud.setHotbar(this.hotbar, i);
    this.hud.showItemName(this.hotbar[i]?.id);
    this.updateHeld();
  }

  private updateHeld() {
    const id = this.heldSprite();
    this.fpItem.visible = !!id;
    this.fpArm.visible = !id;
    if (id && id !== this.fpGeoId) this.fpItem.geometry = itemGeometry(id);
    this.fpGeoId = id;
  }

  /** The sprite for what's in your hand: a bow shows how far back it's pulled. */
  private heldSprite(): string {
    const id = this.hotbar[this.slot]?.id ?? "";
    // Once cast, the line is out in the world rather than dangling off the sprite.
    if (id === "fishing_rod" && this.proj.hasBobber(this.myId)) return "fishing_rod_cast";
    if (id !== "bow" || !this.drawing) return id;
    const pull = this.drawPull();
    return pull >= 0.9 ? "bow_pulling_2" : pull >= 0.65 ? "bow_pulling_1" : "bow_pulling_0";
  }

  /** How far back the bowstring is, 0..1. */
  drawPull(): number {
    return this.drawing ? Math.min(1, (performance.now() - this.drawStart) / 1000 / BOW_FULL_DRAW) : 0;
  }

  /** Left click (desktop) — swing at whatever the crosshair is on. */
  attack(ndc?: THREE.Vector2) {
    if (!this.active) return;
    const now = performance.now();
    if (now - this.lastAttack < 110) return;
    this.lastAttack = now;
    this.handSwing = 0;
    const hit = this.raycast(ndc);
    // Swinging on the way down from a jump is a crit.
    const crit = !this.body.onGround && this.body.vy < 0 && !this.body.inWater;
    if (hit?.kind === "player" && hit.dist <= REACH_HIT) this.send({ t: "attack", target: hit.model.id, crit });
    else if (hit?.kind === "sign" && hit.dist <= REACH_USE) this.send({ t: "breakSign", id: hit.sign.info.id });
    else this.send({ t: "swing" });
  }

  /** Right click (desktop) — open, eat, or place the sign you're holding. */
  use(ndc?: THREE.Vector2) {
    if (!this.active || !this.alive) return;
    const hit = this.raycast(ndc);
    if (hit?.kind === "chest" && hit.dist <= REACH_USE) {
      this.handSwing = 0;
      this.send({ t: "open", chest: hit.chest.info.id });
      return;
    }
    const held = ITEMS[this.hotbar[this.slot]?.id ?? ""];
    if (held?.kind === "rod") return this.castRod(ndc);
    if (held?.kind === "food") {
      if (this.hp >= 20) this.hud.toast("You're already at full health");
      else {
        this.send({ t: "eat" });
        this.sound.play("eat");
      }
      return;
    }
    if (held?.kind === "sign") this.placeSign(hit);
  }

  /** Pocket Edition tap: hit a player, open a chest, or use the held item on a block. */
  tap(ndc: THREE.Vector2) {
    if (!this.active) return;
    const hit = this.raycast(ndc);
    if (hit?.kind === "player" && hit.dist <= REACH_HIT + 0.4) return this.attack(ndc);
    if (hit?.kind === "chest" || ["food", "sign", "rod"].includes(this.holding())) return this.use(ndc);
    this.handSwing = 0;
    this.send({ t: "swing" });
  }

  /** Pocket Edition long-press: break the sign under your finger. */
  hold(ndc: THREE.Vector2) {
    if (!this.active) return;
    const hit = this.raycast(ndc);
    if (hit?.kind === "sign" || hit?.kind === "player") this.attack(ndc);
  }

  /** What kind of thing is in your hand. */
  holding(): string {
    return ITEMS[this.hotbar[this.slot]?.id ?? ""]?.kind ?? "";
  }

  get drawing() {
    return this.drawStart > 0;
  }

  /**
   * Right button (or finger) goes down. A chest in reach still opens; otherwise a
   * bow starts drawing, and anything else is a normal use.
   */
  useDown(ndc?: THREE.Vector2) {
    if (this.holding() === "bow") {
      const hit = this.raycast(ndc);
      if (!(hit?.kind === "chest" && hit.dist <= REACH_USE) && this.startDraw()) return;
    }
    this.use(ndc);
  }

  /** Start pulling the bow back. Needs an arrow somewhere in the hotbar. */
  startDraw(): boolean {
    if (!this.active || !this.alive || this.frozen || this.drawing || this.holding() !== "bow") return false;
    if (!this.hotbar.some((s) => s?.id === "arrow")) return false;
    this.drawStart = performance.now();
    this.send({ t: "draw", on: true });
    this.sound.play("draw");
    return true;
  }

  cancelDraw() {
    if (!this.drawing) return;
    this.drawStart = 0;
    this.send({ t: "draw", on: false });
  }

  /** Let go of the string, aiming at the point under the cursor/finger (screen centre by default). */
  release(ndc?: THREE.Vector2) {
    if (!this.drawing) return;
    const held = (performance.now() - this.drawStart) / 1000;
    this.drawStart = 0;
    if (held < BOW_MIN_DRAW) return this.send({ t: "draw", on: false });
    const [yaw, pitch] = this.aimAngles(ndc);
    this.send({ t: "shoot", yaw, pitch });
  }

  /** Cast the fishing rod (toward that screen point), or reel it in if it's out. */
  private castRod(ndc?: THREE.Vector2) {
    if (this.frozen) return;
    const [yaw, pitch] = this.aimAngles(ndc);
    this.handSwing = 0;
    this.send({ t: "rod", yaw, pitch });
  }

  /** Where a player's fishing line leaves their rod, as drawn on screen. */
  private rodTip = (id: number): THREE.Vector3 | null => {
    if (id === this.myId) {
      if (this.selfModel?.root.visible) return this.selfModel.heldPoint(ROD_TIP[0], ROD_TIP[1]);
      if (!this.viewModel.visible || !this.fpItem.visible) return null;
      this.camera.updateMatrixWorld(true);
      return this.fpItem.localToWorld(new THREE.Vector3(ROD_TIP[0], ROD_TIP[1], 0));
    }
    const m = this.models.get(id);
    return m?.root.visible ? m.heldPoint(ROD_TIP[0], ROD_TIP[1]) : null;
  };

  private playerAt = (id: number): THREE.Vector3 | null => {
    if (id === this.myId) return new THREE.Vector3(this.body.x, this.body.y, this.body.z);
    const m = this.models.get(id);
    return m?.root.visible ? new THREE.Vector3(m.x, m.y, m.z) : null;
  };

  /** Yaw/pitch from your eyes toward whatever is under that screen point. */
  private aimAngles(ndc?: THREE.Vector2): [number, number] {
    if (this.view === 2) return [this.yaw, this.pitch];
    const o = this.camera.getWorldPosition(new THREE.Vector3());
    const d = new THREE.Vector3(ndc?.x ?? 0, ndc?.y ?? 0, 0.5).unproject(this.camera).sub(o).normalize();
    // Behind-the-back camera: aim where the camera ray ends up, not parallel to it.
    const dir = o.addScaledVector(d, 40).sub(this.eyePos()).normalize();
    return [Math.atan2(-dir.x, -dir.z), Math.asin(Math.max(-1, Math.min(1, dir.y)))];
  }

  private placeSign(hit: Hit | null) {
    if (!this.alive) return this.hud.toast("Ghosts can't place signs");
    if (this.frozen) return this.hud.toast("Wait for the countdown to finish");
    if (!hit || hit.kind !== "block" || hit.dist > REACH_USE) return this.hud.toast("Tap a nearby block to place your sign");
    if (hit.ny === -1) return this.hud.toast("Signs can't hang from ceilings");
    const x = hit.x + hit.nx, y = hit.y + hit.ny, z = hit.z + hit.nz;
    let wall = -1;
    if (hit.ny !== 1) wall = hit.nz === 1 ? 0 : hit.nz === -1 ? 1 : hit.nx === 1 ? 2 : 3;
    const rot = ((Math.round((this.yaw / (Math.PI * 2)) * 16) % 16) + 16) % 16;
    this.handSwing = 0;
    this.keys.clear();
    this.signEditor.open((lines) => {
      if (lines) this.send({ t: "sign", x, y, z, wall, rot, lines });
    });
  }

  // ---------- raycast ----------

  raycast(ndc?: THREE.Vector2): Hit | null {
    if (!this.world) return null;
    let o = this.camera.getWorldPosition(new THREE.Vector3());
    let d = new THREE.Vector3(ndc?.x ?? 0, ndc?.y ?? 0, 0.5).unproject(this.camera).sub(o).normalize();
    // Third person still aims from your eyes, so you can't hit things around a corner.
    if (this.view === 1) o = this.eyePos();
    else if (this.view === 2) {
      o = this.eyePos();
      d = this.lookDir();
    }
    let best: Hit | null = null;
    const maxD = 6;

    // Voxel DDA for blocks and chests.
    let x = Math.floor(o.x), y = Math.floor(o.y), z = Math.floor(o.z);
    const sx = Math.sign(d.x), sy = Math.sign(d.y), sz = Math.sign(d.z);
    const tdx = Math.abs(1 / d.x), tdy = Math.abs(1 / d.y), tdz = Math.abs(1 / d.z);
    let tx = (sx > 0 ? x + 1 - o.x : o.x - x) * tdx;
    let ty = (sy > 0 ? y + 1 - o.y : o.y - y) * tdy;
    let tz = (sz > 0 ? z + 1 - o.z : o.z - z) * tdz;
    let n: [number, number, number] = [0, 0, 0];
    let t = 0;
    while (t <= maxD) {
      const chest = this.chestAt.get(this.key(x, y, z));
      if (chest) {
        best = { kind: "chest", chest, dist: t };
        break;
      }
      const b = this.world.get(x, y, z);
      if (b !== B.AIR && b !== B.WATER) {
        best = { kind: "block", x, y, z, nx: n[0], ny: n[1], nz: n[2], dist: t };
        break;
      }
      if (tx < ty && tx < tz) {
        x += sx; t = tx; tx += tdx; n = [-sx, 0, 0];
      } else if (ty < tz) {
        y += sy; t = ty; ty += tdy; n = [0, -sy, 0];
      } else {
        z += sz; t = tz; tz += tdz; n = [0, 0, -sz];
      }
    }

    const consider = (h: Hit) => {
      if (!best || h.dist < best.dist) best = h;
    };
    for (const s of this.signs.values()) {
      const b = signBox(s.info);
      const dist = rayBox(o, d, b.min, b.max);
      if (dist !== null && dist <= maxD) consider({ kind: "sign", sign: s, dist });
    }
    if (this.alive) {
      for (const m of this.models.values()) {
        if (!m.root.visible) continue;
        const w = PLAYER_W / 2 + 0.1;
        const dist = rayBox(o, d, new THREE.Vector3(m.x - w, m.y, m.z - w), new THREE.Vector3(m.x + w, m.y + PLAYER_H, m.z + w));
        if (dist !== null && dist <= maxD) consider({ kind: "player", model: m, dist });
      }
    }
    return best;
  }

  private eyePos() {
    return new THREE.Vector3(this.body.x, this.body.y + EYE, this.body.z);
  }
  private lookDir() {
    const cp = Math.cos(this.pitch);
    return new THREE.Vector3(-Math.sin(this.yaw) * cp, Math.sin(this.pitch), -Math.cos(this.yaw) * cp);
  }

  cycleView() {
    this.view = (this.view + 1) % 3;
  }

  /** Pull the third-person camera in so it never ends up inside a wall. */
  private cameraDistance(from: THREE.Vector3, dir: THREE.Vector3, want: number) {
    for (let t = 0.3; t <= want; t += 0.1) {
      const x = from.x + dir.x * t, y = from.y + dir.y * t, z = from.z + dir.z * t;
      // Check a little box around the lens, not just a point.
      for (const [ox, oy, oz] of [[0, 0, 0], [0.2, 0, 0], [-0.2, 0, 0], [0, 0.2, 0], [0, -0.2, 0], [0, 0, 0.2], [0, 0, -0.2]]) {
        if (this.solid(Math.floor(x + ox!), Math.floor(y + oy!), Math.floor(z + oz!))) return Math.max(0.3, t - 0.2);
      }
    }
    return want;
  }

  // ---------- frame ----------

  frame(dt: number) {
    if (!this.active || !this.world) {
      this.renderer.render(this.scene, this.camera);
      return;
    }
    const typing = this.signEditor.isOpen || this.hud.inventoryOpen || this.hud.chatLogOpen;
    const k = (c: string) => !typing && this.keys.has(c);
    const forward = (k("KeyW") || k("ArrowUp") ? 1 : 0) - (k("KeyS") || k("ArrowDown") ? 1 : 0) + this.touchMove.f;
    const strafe = (k("KeyD") || k("ArrowRight") ? 1 : 0) - (k("KeyA") || k("ArrowLeft") ? 1 : 0) + this.touchMove.s;
    const jump = k("Space") || this.touchMove.jump;
    const sneak = k("ShiftLeft") || k("ShiftRight") || this.sneakToggle;
    // Switched away, died, opened a screen: the string goes slack.
    if (this.drawing && (this.holding() !== "bow" || !this.alive || this.frozen || typing)) this.cancelDraw();
    if (forward <= 0 || sneak || this.drawing) this.sprint = false;
    if (k("ControlLeft") && forward > 0) this.sprint = true;

    const spectating = !this.alive && this.phase !== "waiting" && this.phase !== "countdown";
    const px = this.body.x, pz = this.body.z;
    if (spectating) {
      // Ghosts fly through everything.
      const sp = 9 * dt;
      const sin = Math.sin(this.yaw), cos = Math.cos(this.yaw);
      this.body.x += (-sin * forward + cos * strafe) * sp;
      this.body.z += (-cos * forward - sin * strafe) * sp;
      this.body.y += ((jump ? 1 : 0) - (sneak ? 1 : 0)) * sp;
      this.body.y = Math.max(2, Math.min(60, this.body.y));
    } else if (!this.frozen) {
      // Drawing a bow slows you to a crawl, like Minecraft.
      const speed = (sneak ? SNEAK : this.sprint ? SPRINT : WALK) * (this.drawing ? 0.25 : 1);
      let remaining = Math.min(dt, 0.1);
      while (remaining > 0) {
        const h = Math.min(remaining, 1 / 60);
        remaining -= h;
        const fall = step(this.body, { forward: clamp1(forward), strafe: clamp1(strafe), jump, speed, yaw: this.yaw, autoJump: this.autoJump && !sneak }, h, this.world, this.solid);
        if (fall > 3.5) this.send({ t: "fall", d: fall });
        if (fall > 1.2) this.sound.play("land", undefined, { vol: Math.min(1, fall / 6) });
      }
      if (this.body.y < -5) this.body.y = 30;
    }

    // View bobbing, Minecraft-style: figure-eight sway driven by distance walked.
    const moved = Math.hypot(this.body.x - px, this.body.z - pz);
    const walking = this.body.onGround && !spectating && moved > 0.001;
    this.walkDist += moved * 0.6;
    // A footstep every ~1.7 blocks walked, sounding like whatever is underfoot.
    if (walking && !sneak && this.walkDist - this.stepAt > 1) {
      this.stepAt = this.walkDist;
      this.sound.play("step", undefined, { vol: 0.8, surface: surfaceOf(this.world.get(Math.floor(this.body.x), Math.floor(this.body.y - 0.2), Math.floor(this.body.z))) });
    }
    if (this.body.inWater && !this.wasInWater && !spectating) this.sound.play("splash");
    this.wasInWater = this.body.inWater;
    this.bob += ((walking ? Math.min(1, moved / dt / WALK) : 0) - this.bob) * Math.min(1, dt * 10);
    const f = this.walkDist * Math.PI;
    const bobX = Math.sin(f) * 0.035 * this.bob;
    const bobY = -Math.abs(Math.cos(f)) * 0.06 * this.bob;
    const bobRoll = Math.sin(f) * 0.012 * this.bob;
    const bobPitch = Math.abs(Math.cos(f - 0.2)) * 0.016 * this.bob;
    // Hurt cam: a quick roll that eases back.
    this.hurtT = Math.max(0, this.hurtT - dt * 2);
    const hurtRoll = -Math.sin(Math.pow(this.hurtT, 4) * Math.PI) * 0.22;

    const eye = EYE - (sneak && !spectating ? 0.08 : 0);
    const view = spectating ? 0 : this.view;
    if (view === 0) {
      this.camera.rotation.set(this.pitch - bobPitch, this.yaw, bobRoll + hurtRoll);
      const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
      this.camera.position.set(this.body.x + right.x * bobX, this.body.y + eye + bobY, this.body.z + right.z * bobX);
    } else {
      const head = new THREE.Vector3(this.body.x, this.body.y + eye, this.body.z);
      const look = this.lookDir();
      const away = view === 1 ? look.clone().negate() : look;
      this.camera.position.copy(head).addScaledVector(away, this.cameraDistance(head, away, 4));
      if (view === 1) this.camera.rotation.set(this.pitch, this.yaw, hurtRoll);
      else this.camera.rotation.set(-this.pitch, this.yaw + Math.PI, -hurtRoll);
    }
    this.updateSelfModel(dt, view !== 0 && this.alive && !spectating, moved / Math.max(dt, 1e-3), sneak);

    // Sprinting widens the view a touch.
    // ...and pulling a bow back zooms in a little.
    const targetFov = FOV * (this.sprint && !spectating ? 1.12 : 1) * (1 - 0.15 * this.drawPull() ** 2);
    if (Math.abs(this.fov - targetFov) > 0.05) {
      this.fov += (targetFov - this.fov) * Math.min(1, dt * 10);
      this.camera.fov = this.fov;
      this.camera.updateProjectionMatrix();
    }

    this.updateViewModel(dt, spectating || view !== 0, bobX, bobY);

    const now = performance.now();
    if (now - this.lastSend > 50) {
      this.lastSend = now;
      this.send({ t: "pos", x: r3(this.body.x), y: r3(this.body.y), z: r3(this.body.z), yaw: r3(this.yaw), pitch: r3(this.pitch), sneak });
    }

    this.sound.setListener(this.camera.position.x, this.camera.position.y, this.camera.position.z, this.yaw);
    for (const m of this.models.values()) {
      if (!m.root.visible) continue;
      m.update(dt);
      this.otherFootsteps(m);
    }
    for (const c of this.chests.values()) c.update(dt);
    this.particles.update(dt);
    this.proj.update(dt, this.rodTip, this.playerAt);
    this.sky.position.copy(this.camera.position);
    // Clouds ride along with the camera but scroll so they stay put in the world, plus a slow drift.
    this.cloudDrift += dt * 0.6;
    const cm = this.clouds.material as THREE.MeshBasicMaterial;
    this.clouds.position.x = this.camera.position.x;
    this.clouds.position.z = this.camera.position.z;
    cm.map!.offset.set(((this.camera.position.x + this.cloudDrift) / 768) % 1, (-this.camera.position.z / 768) % 1);

    // Crosshair target: outline + popup hint (desktop only — PE has no crosshair).
    const hit = this.touchMode ? null : this.raycast(this.aim ?? undefined);
    this.updateOutline(hit);
    this.hud.setTarget(
      hit?.kind === "chest" && hit.dist <= REACH_USE ? (hit.chest.open ? "Chest (looted)" : "Chest · right-click to open")
        : hit?.kind === "sign" && hit.dist <= REACH_USE ? `Sign by ${hit.sign.info.by}` : this.updateCompass(now),
    );
    const secs = Math.max(0, Math.ceil((this.phaseEnds - now) / 1000));
    this.hud.setPhase(this.phase, secs, spectating);
    if (this.phase === "countdown" && secs <= 5 && secs > 0 && secs !== this.lastTick) this.sound.play("tick");
    this.lastTick = this.phase === "countdown" ? secs : -1;

    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Points the compass at the nearest living player. Returns the popup line to
   * show while it's in your hand ("" otherwise).
   */
  private updateCompass(now: number): string {
    if (!this.hotbar.some((s) => s?.id === "compass")) return "";
    const living = [...this.models.values()].filter((m) => m.root.visible && this.infos.get(m.id)?.alive);
    const near = nearestPlayer(this.body.x, this.body.z, living);
    // Nobody to track: the needle wanders, like a compass in the Nether.
    this.hud.setCompass(near ? needleAngle(this.body.x, this.body.z, this.yaw, near.p.x, near.p.z) : now / 400);
    if (this.hotbar[this.slot]?.id !== "compass") return "";
    return near ? `Nearest: <b>${near.p.name}</b> (${Math.round(near.dist)}m)` : "Nearest: nobody";
  }

  /** You, as everyone else sees you: only drawn in third person. */
  private updateSelfModel(dt: number, show: boolean, speed: number, sneak: boolean) {
    const m = this.selfModel;
    if (!m) return;
    m.root.visible = show;
    if (!show) return;
    if (this.handSwing < this.selfSwing) m.swing();
    this.selfSwing = this.handSwing;
    m.setHeld(this.heldSprite());
    m.drawing = this.drawing;
    m.sneak = sneak;
    Object.assign(m, { x: this.body.x, y: this.body.y, z: this.body.z, yaw: this.yaw, pitch: this.pitch });
    m.animate(dt, speed);
  }

  /** Other players' footsteps, quieter and from where they are. */
  private otherFootsteps(m: PlayerModel) {
    const s = this.otherSteps.get(m.id) ?? { x: m.x, z: m.z, d: 0 };
    const moved = Math.hypot(m.x - s.x, m.z - s.z);
    s.x = m.x;
    s.z = m.z;
    // Snapshots can jump (teleports, respawns); don't count those as walking.
    if (moved < 1 && !m.sneak) s.d += moved;
    if (s.d > 1.7) {
      s.d = 0;
      const below = this.world!.get(Math.floor(m.x), Math.floor(m.y - 0.2), Math.floor(m.z));
      if (below !== B.AIR && below !== B.WATER) this.sound.play("step", { x: m.x, y: m.y, z: m.z }, { vol: 0.7, surface: surfaceOf(below) });
    }
    this.otherSteps.set(m.id, s);
  }

  private updateViewModel(dt: number, spectating: boolean, bobX: number, bobY: number) {
    this.viewModel.visible = !spectating;
    if (this.handSwing < 1) this.handSwing = Math.min(1, this.handSwing + dt * 5.5);
    if (this.equipT < 1) this.equipT = Math.min(1, this.equipT + dt * 6);
    const s = this.handSwing;
    const swingA = Math.sin(s * Math.PI);
    const swingB = Math.sin(Math.sqrt(s) * Math.PI);
    const drop = (1 - this.equipT) * 0.35;
    // Sway opposite the camera bob, like the real viewmodel.
    this.viewModel.position.set(-bobX * 0.6 - swingB * 0.12, bobY * 0.4 - drop + swingB * 0.05, -swingA * 0.08);
    this.viewModel.rotation.set(-swingA * 0.5, swingB * 0.35, -swingA * 0.15);

    // Bare arm: comes up from the bottom-right, palm side toward the middle.
    this.fpArm.position.set(0.45, -0.43, -0.55);
    this.fpArm.rotation.set(1.55, 0.5, -0.3);
    // Held item: turned edge-on so the blade recedes up toward the crosshair.
    this.fpItem.position.set(0.45, -0.32, -0.8);
    this.fpItem.rotation.set(0.1, -1.0, 0.25);
    this.fpItem.scale.setScalar(0.52);

    // Drawing a bow: it swings in front of you, arrow toward the crosshair, and
    // comes back toward your face as the string is pulled, trembling at full draw.
    this.updateHeld();
    this.drawBlend += ((this.drawing ? 1 : 0) - this.drawBlend) * Math.min(1, dt * 14);
    if (this.drawBlend > 0.01) {
      const k = this.drawBlend, pull = this.drawPull();
      const shake = pull >= 1 ? Math.sin(performance.now() * 0.07) * 0.004 : 0;
      this.fpItem.position.lerp(new THREE.Vector3(0.18 + shake, -0.2 + shake, -0.9 + pull * 0.15), k);
      // Sprite turned so the arrow points in toward the crosshair, grip forward, string at your face.
      const r = this.fpItem.rotation;
      r.set(r.x + (0.25 - r.x) * k, r.y + (-0.6 - r.y) * k, r.z + ((Math.PI * 3) / 4 - r.z) * k);
      this.fpItem.scale.setScalar(0.52 - 0.07 * k);
    }
  }

  private updateOutline(hit: Hit | null) {
    const o = this.outline;
    o.visible = false;
    if (!hit || hit.dist > REACH_USE + 0.5) return;
    if (hit.kind === "block") {
      o.position.set(hit.x + 0.5, hit.y + 0.5, hit.z + 0.5);
      o.scale.set(1.004, 1.004, 1.004);
      o.visible = true;
    } else if (hit.kind === "chest") {
      const i = hit.chest.info;
      o.position.set(i.x + 0.5, i.y + 7.5 / 16, i.z + 0.5);
      o.scale.set(14.1 / 16, 15.1 / 16, 14.1 / 16);
      o.visible = true;
    } else if (hit.kind === "sign") {
      const b = signBox(hit.sign.info);
      o.position.copy(b.min).add(b.max).multiplyScalar(0.5);
      o.scale.copy(b.max).sub(b.min).addScalar(0.004);
      o.visible = true;
    }
  }

  look(dx: number, dy: number) {
    this.yaw -= dx;
    this.pitch = Math.max(-1.55, Math.min(1.55, this.pitch - dy));
  }
}

function signBox(i: SignInfo) {
  const y0 = i.wall === -1 ? i.y + 0.55 : i.y + 0.25;
  return { min: new THREE.Vector3(i.x + 0.1, y0, i.z + 0.1), max: new THREE.Vector3(i.x + 0.9, y0 + 0.55, i.z + 0.9) };
}

function makeSky(): THREE.Group {
  const g = new THREE.Group();
  // Gradient dome: deep blue overhead fading to the fog colour at the horizon.
  const geo = new THREE.SphereGeometry(200, 24, 16);
  const pos = geo.getAttribute("position");
  const cols: number[] = [];
  const c = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i) / 200;
    if (y >= 0) c.copy(HORIZON).lerp(SKY_TOP, Math.min(1, Math.pow(y, 0.55) * 1.15));
    else c.copy(HORIZON).lerp(VOID, Math.min(1, -y * 3));
    cols.push(c.r, c.g, c.b);
  }
  geo.setAttribute("color", new THREE.Float32BufferAttribute(cols, 3));
  const dome = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide, fog: false, depthWrite: false }));
  dome.renderOrder = -10;
  g.add(dome);

  // The square sun.
  const sc = document.createElement("canvas");
  sc.width = sc.height = 32;
  const sg = sc.getContext("2d")!;
  sg.fillStyle = "rgba(255,220,90,0.35)";
  sg.fillRect(4, 4, 24, 24);
  sg.fillStyle = "rgba(255,240,150,0.7)";
  sg.fillRect(8, 8, 16, 16);
  sg.fillStyle = "#fffbe8";
  sg.fillRect(10, 10, 12, 12);
  const sun = new THREE.Mesh(
    new THREE.PlaneGeometry(36, 36),
    new THREE.MeshBasicMaterial({ map: pixelTexture(sc), transparent: true, fog: false, depthWrite: false, blending: THREE.AdditiveBlending }),
  );
  const dir = new THREE.Vector3(-0.45, 0.75, -0.5).normalize();
  sun.position.copy(dir).multiplyScalar(170);
  sun.lookAt(0, 0, 0);
  sun.renderOrder = -9;
  g.add(sun);
  return g;
}

function makeClouds(): THREE.Mesh {
  // Classic cloud map: chunky blobs, one texel = 12 blocks.
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d")!;
  const cells = new Float32Array(64 * 64).map(() => Math.random());
  const at = (x: number, y: number) => cells[((y + 64) % 64) * 64 + ((x + 64) % 64)]!;
  g.fillStyle = "#ffffff";
  for (let y = 0; y < 64; y++) for (let x = 0; x < 64; x++) {
    let v = 0;
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) v += at(x + dx, y + dy);
    if (v / 25 > 0.535) g.fillRect(x, y, 1, 1);
  }
  const tex = pixelTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(768, 768),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.8, fog: false, depthWrite: false, side: THREE.DoubleSide }),
  );
  m.rotation.x = -Math.PI / 2;
  m.position.y = 62;
  m.renderOrder = -8;
  return m;
}

function clamp1(v: number) {
  return Math.max(-1, Math.min(1, v));
}
function r3(n: number) {
  return Math.round(n * 1000) / 1000;
}

function rayBox(o: THREE.Vector3, d: THREE.Vector3, min: THREE.Vector3, max: THREE.Vector3): number | null {
  let tmin = -Infinity, tmax = Infinity;
  const os = [o.x, o.y, o.z], ds = [d.x, d.y, d.z], mins = [min.x, min.y, min.z], maxs = [max.x, max.y, max.z];
  for (let i = 0; i < 3; i++) {
    if (Math.abs(ds[i]!) < 1e-9) {
      if (os[i]! < mins[i]! || os[i]! > maxs[i]!) return null;
      continue;
    }
    let a = (mins[i]! - os[i]!) / ds[i]!;
    let b = (maxs[i]! - os[i]!) / ds[i]!;
    if (a > b) [a, b] = [b, a];
    tmin = Math.max(tmin, a);
    tmax = Math.min(tmax, b);
    if (tmin > tmax) return null;
  }
  if (tmax < 0) return null;
  return Math.max(0, tmin);
}
